import React from 'react';

interface PaginationProps {
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  itemsPerPage: number;
  totalItems: number;
}

const Pagination: React.FC<PaginationProps> = ({
  currentPage,
  totalPages,
  onPageChange,
  itemsPerPage,
  totalItems
}) => {
  if (totalPages <= 1) {
    return null; // ページが1つしかない場合は表示しない
  }

  const getPageNumbers = () => {
    const pages: (number | string)[] = [];
    const maxVisiblePages = 5;

    if (totalPages <= maxVisiblePages) {
      // 全ページを表示
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      // 省略表示
      if (currentPage <= 3) {
        // 最初の方
        for (let i = 1; i <= 4; i++) {
          pages.push(i);
        }
        pages.push('...');
        pages.push(totalPages);
      } else if (currentPage >= totalPages - 2) {
        // 最後の方
        pages.push(1);
        pages.push('...');
        for (let i = totalPages - 3; i <= totalPages; i++) {
          pages.push(i);
        }
      } else {
        // 中間
        pages.push(1);
        pages.push('...');
        for (let i = currentPage - 1; i <= currentPage + 1; i++) {
          pages.push(i);
        }
        pages.push('...');
        pages.push(totalPages);
      }
    }

    return pages;
  };

  const startItem = (currentPage - 1) * itemsPerPage + 1;
  const endItem = Math.min(currentPage * itemsPerPage, totalItems);

  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      gap: '15px',
      padding: 'clamp(10px, 3vw, 20px)',
      backgroundColor: 'white',
      borderRadius: '4px',
      border: '1px solid #dee2e6',
      marginTop: '20px'
    }}>
      {/* 表示件数情報 */}
      <div style={{ fontSize: 'clamp(12px, 2.5vw, 14px)', color: '#6c757d', textAlign: 'center' }}>
        {totalItems}件中 {startItem}〜{endItem}件を表示
      </div>

      {/* ページネーションボタン */}
      <div style={{ display: 'flex', gap: '5px', alignItems: 'center', justifyContent: 'center', flexWrap: 'wrap' }}>
        {/* 前へボタン */}
        <button
          onClick={() => onPageChange(currentPage - 1)}
          disabled={currentPage === 1}
          style={{
            padding: 'clamp(6px, 1.5vw, 8px) clamp(10px, 2.5vw, 12px)',
            backgroundColor: currentPage === 1 ? '#e9ecef' : '#007bff',
            color: currentPage === 1 ? '#6c757d' : 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
            fontSize: 'clamp(12px, 2.5vw, 14px)',
            fontWeight: 'bold',
            whiteSpace: 'nowrap'
          }}
        >
          前へ
        </button>

        {/* ページ番号ボタン */}
        {getPageNumbers().map((page, index) => {
          if (page === '...') {
            return (
              <span key={`ellipsis-${index}`} style={{
                padding: 'clamp(6px, 1.5vw, 8px)',
                fontSize: 'clamp(12px, 2.5vw, 14px)',
                color: '#6c757d'
              }}>
                ...
              </span>
            );
          }

          const pageNum = page as number;
          return (
            <button
              key={pageNum}
              onClick={() => onPageChange(pageNum)}
              style={{
                padding: 'clamp(6px, 1.5vw, 8px) clamp(10px, 2.5vw, 12px)',
                backgroundColor: currentPage === pageNum ? '#007bff' : 'white',
                color: currentPage === pageNum ? 'white' : '#007bff',
                border: '1px solid #007bff',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: 'clamp(12px, 2.5vw, 14px)',
                fontWeight: currentPage === pageNum ? 'bold' : 'normal',
                minWidth: 'clamp(30px, 8vw, 36px)'
              }}
            >
              {pageNum}
            </button>
          );
        })}

        {/* 次へボタン */}
        <button
          onClick={() => onPageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
          style={{
            padding: 'clamp(6px, 1.5vw, 8px) clamp(10px, 2.5vw, 12px)',
            backgroundColor: currentPage === totalPages ? '#e9ecef' : '#007bff',
            color: currentPage === totalPages ? '#6c757d' : 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
            fontSize: 'clamp(12px, 2.5vw, 14px)',
            fontWeight: 'bold',
            whiteSpace: 'nowrap'
          }}
        >
          次へ
        </button>
      </div>
    </div>
  );
};

export default Pagination;
